/*
 * Copyright (c) Arbeidskracht Groep B.V. - all rights reserved
 */

import {
	ActionTypes,
	loginSceneAccountNameInputChanged,
	loginSceneActivated,
	loginSceneAuthenticationAborted,
	loginSceneAuthenticationBlocked,
	loginSceneAuthenticationExpired,
	loginSceneAuthenticationInvalidCredentials,
	loginSceneAuthenticationNotFound,
	loginSceneAuthenticationStarted,
	loginSceneChangePasswordValidationException,
	loginSceneCredentialsSubmitted,
	loginSceneEmailOwnershipDeniedForFlexServiceWorkerRegistration,
	loginSceneEnterPhoneVerificationCodeValidationException,
	loginSceneFlexServiceWorkerRegistrationCompleted,
	loginSceneFlexServiceWorkerRegistrationExpired,
	loginSceneFlexServiceWorkerRegistrationFailed,
	loginSceneNewPasswordInputChanged,
	loginSceneNewPasswordStrengthEvaluated,
	loginSceneNewPasswordSubmitted,
	loginScenePasswordChangeRequired,
	loginScenePasswordInputChanged,
	loginScenePasswordNotChangedWithInsufficientStrength,
	loginScenePasswordNotChangedWithSamePassword,
	loginScenePhoneVerificationCodeInputChanged,
	loginScenePhoneVerificationCodeSubmitted,
	loginScenePhoneVerificationFailed,
	loginScenePhoneVerificationStartBlocked,
	loginScenePhoneVerificationStartBusy,
	loginScenePhoneVerificationStarted,
	loginScenePhoneVerificationStartFailed,
	loginScenePhoneVerificationStopped,
	loginSceneRememberMeTokenNotOk,
	loginSceneRequestRestartPhoneVerification,
	loginSceneRequestRetryPhoneVerification,
	loginSceneRetryPhoneVerificationUnexpectedError,
	loginSceneStartAuthenticationUnexpectedError,
	loginSceneStartAuthenticationValidationException
} from './actions';
import {getType} from 'typesafe-actions';
import {combineReducers} from 'redux';
import {ConstraintViolationDTO} from '../../model/validation';
import {DisplayType} from './displayType';
import PasswordCriteriaDTO from '../../services/Stomp/PasswordCriteriaDTO';
import {MessageType} from '../../model/messageType';

export interface CredentialsState {
	submitDisabled: boolean;
	violations: { [key: string]: ConstraintViolationDTO[] };
}

const credentialsInitialState: CredentialsState = {
	submitDisabled: false,
	violations: {}
};

export interface EnterPasswordState {
	email: string;
	minimumStrength: number;
	strength: number;
	submitDisabled: boolean;
	violations: { [key: string]: ConstraintViolationDTO[] };
}

const enterPasswordInitialState: EnterPasswordState = {
	email: '',
	minimumStrength: 0,
	strength: 0,
	submitDisabled: false,
	violations: {}
};

export interface IndexState {
	displayType: DisplayType;
	message: string;
	messageType: MessageType;
	passwordCriteria: PasswordCriteriaDTO | null;
	renewedPhoneVerification: boolean;
	twoFactorAuthenticationId: string | null;
}

const initialIndexState: IndexState = {
	displayType: DisplayType.DISPLAY_TYPE_REMEMBER_ME,
	message: '',
	messageType: MessageType.INFO,
	passwordCriteria: null,
	renewedPhoneVerification: false,
	twoFactorAuthenticationId: null
};

export interface RetryPhoneVerificationStartState {
	buttonText: string;
	submitDisabled: boolean;
}

const retryPhoneVerificationStartInitialState: RetryPhoneVerificationStartState = {
	buttonText: '',
	submitDisabled: false
};

export interface VerificationCodeState {
	maximumDurationInSeconds: number | null;
	submitDisabled: boolean;
	verificationStopped: boolean;
	violations: { [key: string]: ConstraintViolationDTO[] };
}

const verificationCodeInitialState: VerificationCodeState = {
	maximumDurationInSeconds: null,
	submitDisabled: false,
	verificationStopped: false,
	violations: {}
};

export interface LoginState {
	credentials: CredentialsState;
	enterPassword: EnterPasswordState;
	index: IndexState;
	retryPhoneVerificationStart: RetryPhoneVerificationStartState;
	verificationCode: VerificationCodeState;
}

const credentialsReducer = (
	state: CredentialsState = credentialsInitialState,
	action: ActionTypes
): CredentialsState => {
	switch (action.type) {
		case getType(loginSceneActivated):
			return credentialsInitialState;
		case getType(loginSceneEmailOwnershipDeniedForFlexServiceWorkerRegistration):
		case getType(loginSceneFlexServiceWorkerRegistrationFailed):
		case getType(loginSceneFlexServiceWorkerRegistrationCompleted):
		case getType(loginSceneFlexServiceWorkerRegistrationExpired):
		case getType(loginSceneAuthenticationBlocked):
		case getType(loginSceneAuthenticationInvalidCredentials):
		case getType(loginSceneStartAuthenticationUnexpectedError):
		case getType(loginScenePhoneVerificationStartBlocked):
		case getType(loginSceneAuthenticationNotFound):
		case getType(loginScenePhoneVerificationStopped):
		case getType(loginScenePhoneVerificationStartBusy):
			return {...state, submitDisabled: false};
		case getType(loginSceneAccountNameInputChanged):
			return {...state, violations: {...state.violations, accountName: []}};
		case getType(loginScenePasswordInputChanged):
			return {...state, violations: {...state.violations, password: []}};
		case getType(loginSceneCredentialsSubmitted):
			return {...state, submitDisabled: true, violations: {}};
		case getType(loginSceneStartAuthenticationValidationException):
			return {...state, submitDisabled: false, violations: action.payload.violations};
		case getType(loginSceneAuthenticationExpired):
		case getType(loginSceneAuthenticationAborted):
			return {...state, submitDisabled: false, violations: {}};
		default:
			return state;
	}
};

const enterPasswordReducer = (
	state: EnterPasswordState = enterPasswordInitialState,
	action: ActionTypes
): EnterPasswordState => {
	switch (action.type) {
		case getType(loginSceneActivated):
			return enterPasswordInitialState;
		case getType(loginSceneCredentialsSubmitted):
			return {
				...state,
				email: action.payload.command.accountName
			};
		case getType(loginScenePasswordChangeRequired):
			return {
				...state,
				minimumStrength: action.payload.passwordCriteria.minimumPasswordStrength.strength,
				submitDisabled: false,
				violations: {}
			};
		case getType(loginSceneNewPasswordInputChanged):
			return {...state, violations: {}};
		case getType(loginSceneNewPasswordSubmitted):
			return {...state, submitDisabled: true, violations: {}};
		case getType(loginSceneNewPasswordStrengthEvaluated):
			return {...state, strength: action.payload.passwordStrength.strength};
		case getType(loginSceneChangePasswordValidationException):
			return {...state, submitDisabled: false, violations: action.payload.violations};
		case getType(loginScenePasswordNotChangedWithSamePassword):
			return {
				...state,
				submitDisabled: false,
				violations: {password: [{message: 'Het nieuwe wachtwoord mag niet gelijk zijn aan het huidige wachtwoord'}]}
			};
		case getType(loginScenePasswordNotChangedWithInsufficientStrength):
			return {
				...state,
				minimumStrength: action.payload.passwordCriteria.minimumPasswordStrength.strength,
				strength: action.payload.evaluatedStrength.strength,
				submitDisabled: false,
				violations: {password: [{message: 'Het nieuwe wachtwoord is niet sterk (veilig) genoeg'}]}
			};
		default:
			return state;
	}
};

const indexReducer = (
	state: IndexState = initialIndexState,
	action: ActionTypes
): IndexState => {
	switch (action.type) {
		case getType(loginSceneActivated):
			return initialIndexState;
		case getType(loginSceneEmailOwnershipDeniedForFlexServiceWorkerRegistration):
			return {
				...state,
				message: 'Uw registratie is nog niet voltooid. U kunt nog niet inloggen',
				messageType: MessageType.ERROR
			};
		case getType(loginSceneFlexServiceWorkerRegistrationFailed):
			return {
				...state,
				message: 'Er is iets mis gegaan, begin opnieuw',
				messageType: MessageType.ERROR
			};
		case getType(loginSceneFlexServiceWorkerRegistrationExpired):
			return {
				...state,
				message: 'We hebben enige tijd geen activiteit gezien, begin opnieuw',
				messageType: MessageType.ERROR
			};
		case getType(loginSceneRememberMeTokenNotOk):
			return {...state, displayType: DisplayType.DISPLAY_TYPE_CREDENTIALS};
		case getType(loginSceneCredentialsSubmitted):
			return {...state, messageType: MessageType.INFO, message: ''};
		case getType(loginSceneAuthenticationBlocked):
			return {...state, messageType: MessageType.ERROR, message: 'Om veiligheidsredenen is uw account geblokkeerd'};
		case getType(loginSceneAuthenticationInvalidCredentials):
			return {...state, messageType: MessageType.ERROR, message: 'Ongeldige combinatie van gebruikersnaam en wachtwoord'};
		case getType(loginSceneAuthenticationStarted):
			return {...state, twoFactorAuthenticationId: action.payload.twoFactorAuthenticationId};
		case getType(loginScenePhoneVerificationStartFailed):
			return {
				...state,
				displayType: DisplayType.DISPLAY_TYPE_RETRY_PHONE_VERIFICATION_START,
				message: 'Er is een technisch probleem met het verificatie proces. Wij kunnen op dit moment geen code sturen',
				messageType: MessageType.ERROR,
				renewedPhoneVerification: false
			};
		case getType(loginScenePhoneVerificationStartBlocked):
			return {
				...state,
				displayType: DisplayType.DISPLAY_TYPE_CREDENTIALS,
				message: 'Uw telefoon is geblokkeerd, neem contact met ons op',
				messageType: MessageType.ERROR
			};
		case getType(loginSceneAuthenticationNotFound):
			return {
				...state,
				displayType: DisplayType.DISPLAY_TYPE_CREDENTIALS,
				message: 'Er is een technische fout opgetreden. Probeer opnieuw in te loggen',
				messageType: MessageType.ERROR,
				twoFactorAuthenticationId: null
			};
		case getType(loginScenePhoneVerificationStarted):
			return {
				...state,
				displayType: DisplayType.DISPLAY_TYPE_VERIFICATION_CODE,
				message: state.renewedPhoneVerification
					? 'We hebben een nieuwe code verzonden naar uw telefoon. De vorige code is niet meer geldig'
					: 'We hebben een verificatiecode verzonden naar uw telefoon',
				messageType: MessageType.INFO,
				renewedPhoneVerification: false
			};
		case getType(loginScenePhoneVerificationStartBusy):
			return {
				...state,
				displayType: DisplayType.DISPLAY_TYPE_CREDENTIALS,
				message: 'U heeft in een ander scherm de verificatiecode van uw telefoon ingevoerd. ' +
					'Daarom moet u in dit scherm opnieuw beginnen met inloggen',
				messageType: MessageType.INFO,
				passwordCriteria: null,
				renewedPhoneVerification: false,
				twoFactorAuthenticationId: null
			};
		case getType(loginScenePhoneVerificationFailed):
			return {
				...state,
				message: 'Er is een technische fout opgetreden tijdens het verifiëren van uw code. De code is nog steeds geldig. ' +
					'U kunt het opnieuw proberen',
				messageType: MessageType.ERROR
			};
		case getType(loginScenePhoneVerificationStopped):
			if (action.payload.blocked)
				return {
					...state,
					displayType: DisplayType.DISPLAY_TYPE_CREDENTIALS,
					message: 'Uw telefoon is geblokkeerd, neem contact met ons op',
					messageType: MessageType.ERROR
				};

			if (action.payload.reason === 'CLIENT_REQUESTED_STOP')
				return {...state, renewedPhoneVerification: !action.payload.blocked};

			return {
				...state,
				message: '',
				messageType: MessageType.INFO,
				renewedPhoneVerification: true
			};
		case getType(loginScenePasswordChangeRequired):
			return {
				...state,
				displayType: DisplayType.DISPLAY_TYPE_ENTER_PASSWORD,
				message: 'Om veiligheidsredenen moet het wachtwoord worden gewijzigd',
				messageType: MessageType.INFO,
				passwordCriteria: action.payload.passwordCriteria
			};
		case getType(loginScenePasswordNotChangedWithInsufficientStrength):
			return {
				...state,
				passwordCriteria: action.payload.passwordCriteria
			};
		case getType(loginSceneAuthenticationExpired):
		case getType(loginSceneAuthenticationAborted):
			return {
				...state,
				displayType: DisplayType.DISPLAY_TYPE_CREDENTIALS,
				message: '',
				messageType: MessageType.INFO,
				passwordCriteria: null,
				renewedPhoneVerification: false,
				twoFactorAuthenticationId: null
			};
		default:
			return state;
	}
};

const retryPhoneVerificationStartReducer = (
	state: RetryPhoneVerificationStartState = retryPhoneVerificationStartInitialState,
	action: ActionTypes
): RetryPhoneVerificationStartState => {
	switch (action.type) {
		case getType(loginSceneActivated):
			return retryPhoneVerificationStartInitialState;
		case getType(loginScenePhoneVerificationStartFailed):
		case getType(loginSceneRetryPhoneVerificationUnexpectedError):
			return {...state, submitDisabled: false, buttonText: 'Probeer opnieuw'};
		case getType(loginSceneRequestRetryPhoneVerification):
			return {...state, submitDisabled: true};
		default:
			return state;
	}
};

const verificationCodeReducer = (
	state: VerificationCodeState = verificationCodeInitialState,
	action: ActionTypes
): VerificationCodeState => {
	let violationMessage = '';
	switch (action.type) {
		case getType(loginSceneActivated):
			return verificationCodeInitialState;
		case getType(loginScenePhoneVerificationStarted):
			return {
				...state,
				maximumDurationInSeconds: Math.max(1, action.payload.timeRemainingInSeconds),
				submitDisabled: false,
				verificationStopped: false
			};
		case getType(loginScenePhoneVerificationStopped):
			switch (action.payload.reason) {
				case 'INVALID_VERIFICATION_CODE':
					violationMessage = 'De ingevoerde verificatiecode was niet geldig';
					break;
				case 'VERIFICATION_TIMEOUT':
					violationMessage = 'Uw verificatiecode is verlopen';
					break;
				case 'CLIENT_REQUESTED_STOP':
					return {...state, verificationStopped: true, maximumDurationInSeconds: null};
				default:
					throw new Error('Unrecognized stop reason ' + action.payload.reason);
			}
			return {
				...state,
				maximumDurationInSeconds: null,
				submitDisabled: false,
				verificationStopped: true,
				violations: {verificationCode: [{message: violationMessage}]}
			};
		case getType(loginScenePhoneVerificationCodeInputChanged):
			return {...state, violations: {...state.violations, verificationCode: []}};
		case getType(loginScenePhoneVerificationCodeSubmitted):
		case getType(loginSceneRequestRestartPhoneVerification):
		case getType(loginSceneRequestRetryPhoneVerification):
			return {...state, submitDisabled: true, violations: {}};
		case getType(loginSceneEnterPhoneVerificationCodeValidationException):
			return {...state, submitDisabled: false, violations: action.payload.violations};
		case getType(loginSceneRetryPhoneVerificationUnexpectedError):
		case getType(loginScenePhoneVerificationFailed):
			return {...state, submitDisabled: false};
		default:
			return state;
	}
};

export default combineReducers({
	credentials: credentialsReducer,
	enterPassword: enterPasswordReducer,
	index: indexReducer,
	retryPhoneVerificationStart: retryPhoneVerificationStartReducer,
	verificationCode: verificationCodeReducer
});
